/**
 * Returns initials from a full name string.
 */
export const initials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

/**
 * Computes win rate percentage.
 */
export const winRate = (wins, total) =>
  total > 0 ? Math.round((wins / total) * 100) : 0;

// Statuses that carry no score at all — excluded from W/D/L but still counted
// as "participated" so the player appears in game lists.
const NO_SCORE_STATUSES = new Set(['scheduled', 'postponed', 'cancelled']);

/**
 * Calculates team game stats for a given entityId across ALL statuses.
 *
 * W/D/L and goals are only computed when a real score exists
 * (i.e. status is NOT scheduled / postponed / cancelled).
 * The game still increments `total` so the count reflects every fixture.
 *
 * Handles both raw ObjectId strings and populated { _id } objects.
 */
export const computeGameStats = (games = [], entityId) => {
  const id = entityId?.toString();
  let total = 0, wins = 0, draws = 0, losses = 0, goalsScored = 0, goalsConceded = 0;

  games.forEach((game) => {
    const e1id = (game.entity1?._id ?? game.entity1)?.toString();
    const e2id = (game.entity2?._id ?? game.entity2)?.toString();

    const isEntity1 = e1id === id;
    const isEntity2 = e2id === id;

    if (!isEntity1 && !isEntity2) return; // doesn't involve this entity

    total++;

    // Only compute score-based stats when the game has a real result
    if (NO_SCORE_STATUSES.has(game.status)) return;

    const myScore    = isEntity1 ? (game.score?.entity1 ?? 0) : (game.score?.entity2 ?? 0);
    const theirScore = isEntity1 ? (game.score?.entity2 ?? 0) : (game.score?.entity1 ?? 0);

    goalsScored   += myScore;
    goalsConceded += theirScore;

    if (myScore > theirScore)        wins++;
    else if (myScore === theirScore) draws++;
    else                             losses++;
  });

  return { total, wins, draws, losses, goalsScored, goalsConceded };
};

/**
 * Aggregates full per-player stats across ALL games (any status).
 * Scans every game regardless of the player's entityId —
 * their userId is the identifier used throughout goals / Faults / inGamePenalties.
 *
 * Returned Map: userId → {
 *   goals, assists,
 *   yellowCards, redCards,
 *   penaltiesScored, penaltiesMissed,
 *   gamesPlayed        ← games where their userId appears in players[]
 * }
 */
export const computePlayerGoalStats = (games = [], entityId) => {
  const entityStr = entityId?.toString();
  const stats = new Map(); // userId → stat object

  const ensure = (rawId) => {
    const key = (rawId?._id ?? rawId)?.toString();
    if (!key) return null;
    if (!stats.has(key)) {
      stats.set(key, {
        goals: 0, assists: 0,
        yellowCards: 0, redCards: 0,
        penaltiesScored: 0, penaltiesMissed: 0,
        gamesPlayed: 0,
      });
    }
    return key;
  };

  games.forEach((game) => {
    // ── Games played: count if player's userId is in game.players for our entity ──
    const entityPlayers = (game.players || []).find(
      (p) => (p.entityId?._id ?? p.entityId)?.toString() === entityStr
    );
    const participatingIds = new Set(
      (entityPlayers?.userIds || []).map((u) => (u?._id ?? u)?.toString())
    );
    participatingIds.forEach((uid) => {
      const k = ensure(uid);
      if (k) stats.get(k).gamesPlayed += 1;
    });

    // ── Goals & assists (only our entity's goals) ──
    (game.goals || []).forEach((goal) => {
      const forOurEntity =
        (goal.entityId?._id ?? goal.entityId)?.toString() === entityStr;
      if (!forOurEntity) return;

      const scorerKey = ensure(goal.scoredBy);
      if (scorerKey) stats.get(scorerKey).goals += 1;

      const assistKey = ensure(goal.assistedBy);
      if (assistKey) stats.get(assistKey).assists += 1;
    });

    // ── Yellow & red cards (Faults) — scan ALL players regardless of entity ──
    (game.Faults || []).forEach((fault) => {
      const uid = ensure(fault.userId);
      if (!uid) return;
      if (fault.CardType === 'Yellow') stats.get(uid).yellowCards += 1;
      if (fault.CardType === 'Red')    stats.get(uid).redCards    += 1;
    });

    // ── In-game penalties — scan ALL players regardless of entity ──
    (game.inGamePenalties || []).forEach((pen) => {
      const uid = ensure(pen.userId);
      if (!uid) return;
      if (pen.goalScored) stats.get(uid).penaltiesScored += 1;
      else                stats.get(uid).penaltiesMissed += 1;
    });
  });

  return stats;
};

/**
 * Returns status color for events.
 */
export const eventStatusColor = (status, C) => ({
  active:    C.emerald,
  open:      C.accent,
  completed: C.muted,
  cancelled: C.rose,
  draft:     C.dim,
  close:     C.dim,
  review:    C.amber,
})[status] || C.muted;